/**
 * The new favorites (pins) order after a reorder gesture, for
 * `usePinMutations().reorder`, from the sidebar's Favorites section.
 */

import { useState } from 'react';

/** `dragId` dropped on `targetId`'s row takes its place; null when nothing changes. */
export function pinIdsAfterDrop(
  ids: ReadonlyArray<string>,
  dragId: string,
  targetId: string,
): string[] | null {
  const from = ids.indexOf(dragId);
  const to = ids.indexOf(targetId);
  if (from === -1 || to === -1) return null;
  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, dragId);
  return next.some((id, i) => id !== ids[i]) ? next : null;
}

/** The pin at `index` swapped with its neighbor above (-1) or below (1); null past an end. */
export function pinIdsAfterStep(
  ids: ReadonlyArray<string>,
  index: number,
  direction: -1 | 1,
): string[] | null {
  const target = index + direction;
  if (target < 0 || target >= ids.length) return null;
  const next = [...ids];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** One row's reorder callbacks; the row owns the DOM drag events around them. */
export interface PinRowDrag {
  readonly onDragStart: () => void;
  readonly onDragEnter: () => void;
  /** Drops the dragged pin in this row's place (nothing if none or no change). */
  readonly onDrop: () => void;
  readonly onDragEnd: () => void;
  /** A drop here lands above this row: draw its insertion line. */
  readonly showDropLine: boolean;
}

/**
 * The favorites reorder state both lists share: which pin is being dragged,
 * which row it is over, each row's drag callbacks and the keyboard `step`,
 * all sending the whole new order through `reorder` (`usePinMutations`).
 */
export function usePinReorder(
  pins: ReadonlyArray<{ readonly id: string }>,
  reorder: { readonly mutate: (ids: string[]) => void },
) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const ids = pins.map((p) => p.id);

  const clearDrag = () => {
    setDragId(null);
    setOverId(null);
  };

  const rowDrag = (id: string): PinRowDrag => ({
    onDragStart: () => setDragId(id),
    onDragEnter: () => setOverId(id),
    onDrop: () => {
      const next = dragId ? pinIdsAfterDrop(ids, dragId, id) : null;
      clearDrag();
      if (next) reorder.mutate(next);
    },
    onDragEnd: clearDrag,
    showDropLine: overId === id && dragId !== null && dragId !== id,
  });

  /** Swap the pin at `index` with its neighbor (keyboard reorder); nothing past an end. */
  const step = (index: number, direction: -1 | 1) => {
    const next = pinIdsAfterStep(ids, index, direction);
    if (next) reorder.mutate(next);
  };

  return { dragId, rowDrag, step };
}
